import { useState } from 'react';
import type { ReactNode } from 'react';
import Image from 'next/legacy/image';
import {
  PlayerCircle,
  LoadingOverlay,
  ClubBadgeCircle,
  CONTENT_BLUR,
  CONTROL_SIZE,
  LockCircle,
  PLAYER_CIRCLE_SIZE,
  SILHOUETTE_FILTER
} from './player-circle';
import {
  BodySmallRegularBold,
  CaptionRegular
} from '@components/atoms/typography';

const RemovePlayerButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    aria-label="Remove player"
    onClick={onClick}
    className="flex items-center justify-center rounded-full bg-danger-600 text-white shadow-md pointer"
    style={{ width: CONTROL_SIZE, height: CONTROL_SIZE }}
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  </button>
);

// Captain armband marker shown on the read-only reveal pitch, in the slot the
// remove button occupies on the interactive pitch (bottom-right).
const CaptainBadge = () => (
  <span
    aria-label="Captain"
    className="flex items-center justify-center rounded-full bg-primary-500 text-white shadow-md font-bold leading-none"
    style={{ width: CONTROL_SIZE, height: CONTROL_SIZE, fontSize: 12 }}
  >
    C
  </span>
);

// Name + price label below the portrait. Always renders both lines (with
// non-breaking-space fallbacks) so an `invisible` instance can reserve the
// exact same height in an empty slot, keeping the portrait from shifting
// vertically when a player is selected.
//
// `blurred` hazes the text alone: the green box keeps its edges, so the strap
// still looks like a name strap with a name in it (FFH-2409).
//
// `compact` holds the strap at its narrow width instead of widening at `md`.
// The wide strap assumes the full-width pitch; somewhere two tokens share a
// narrow card, taking the desktop upsize overruns the card (FFH-2409).
export const PlayerInfo = ({
  name,
  price,
  teamCode,
  invisible = false,
  blurred = false,
  compact = false
}: {
  name?: string;
  price?: string;
  teamCode?: string;
  invisible?: boolean;
  blurred?: boolean;
  compact?: boolean;
}) => (
  <div
    aria-hidden={invisible || undefined}
    className={`mt-2 rounded-lg bg-success-800 flex flex-col items-center justify-center py-1 px-1 ${
      compact ? '' : 'md:px-2'
    } ${invisible ? 'invisible' : ''}`}
  >
    <BodySmallRegularBold
      className={`text-center w-16 text-white mb-0 truncate text-xxs ${
        compact ? '' : 'md:w-24 md:text-xs'
      } ${blurred ? CONTENT_BLUR : ''}`}
    >
      {name ?? ' '}
    </BodySmallRegularBold>
    <div className={`flex items-center ${blurred ? CONTENT_BLUR : ''}`}>
      {/* Price and its separator dot render together, so a priceless player
          (e.g. the locked-transfer card) shows a centred team code. The line
          itself always renders to keep the reserved height. */}
      {price && (
        <>
          <CaptionRegular className="text-center text-white mb-0 truncate">
            {price}
          </CaptionRegular>
          <div className="w-1 h-1 bg-neutral-400 rounded mx-1" />
        </>
      )}
      <CaptionRegular className="text-center text-white mb-0 truncate">
        {teamCode ?? ' '}
      </CaptionRegular>
    </div>
  </div>
);

type PitchPlayerProps = {
  name: string;
  teamCode: string;
  price?: string;
  imageUrl: string | null;
  onCancel?: () => void;
  /** Show the captain "C" armband (read-only reveal pitch). */
  isCaptain?: boolean;
  /** Highlight ring around the portrait (interactive selection). */
  isSelected?: boolean;
  /** Optional chip rendered above the portrait (e.g. verdict flags). */
  topBadge?: ReactNode;
  /**
   * Withhold the player's identity: portrait and name strap hazed, club crest
   * replaced by a padlock (FFH-2409). Only the contents blur — every frame
   * around them stays sharp, so the token reads as a player being held back
   * rather than a rendering fault.
   */
  isBlurred?: boolean;
  /** Keep the name strap narrow rather than widening it at `md`. */
  isCompact?: boolean;
};

const PitchPlayer = ({
  name,
  teamCode,
  price,
  imageUrl,
  onCancel,
  isCaptain = false,
  isSelected = false,
  topBadge,
  isBlurred = false,
  isCompact = false
}: PitchPlayerProps) => {
  const [hasImageFailed, setHasImageFailed] = useState(false);
  const [isPortraitLoaded, setIsPortraitLoaded] = useState(false);
  const showPortrait = Boolean(imageUrl) && !hasImageFailed;

  const cornerMarker = onCancel ? (
    <RemovePlayerButton onClick={onCancel} />
  ) : isCaptain ? (
    <CaptainBadge />
  ) : null;

  return (
    <div className="flex justify-center z-10">
      <div className="flex flex-col items-center">
        <div className="relative z-10">
          {topBadge && (
            <div className="absolute -top-4 left-1/2 z-20 -translate-x-1/2">
              {topBadge}
            </div>
          )}
          <PlayerCircle hideSilhouette={isPortraitLoaded} selected={isSelected}>
            {/* Under a blurred portrait, the same portrait flattened to solid
                grey. The blur samples transparency past the image's edge, and
                against bare white that reads as a halo; against a silhouette
                cut to this player's own outline it reads as one figure with
                its face hidden. The generic silhouette cannot do the job — it
                is somebody else's shape, so the blur spills past it. */}
            {isBlurred && imageUrl && !hasImageFailed && (
              <div
                aria-hidden
                className={`absolute inset-0 transition-opacity duration-300 ${
                  isPortraitLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ filter: SILHOUETTE_FILTER }}
              >
                <Image
                  src={imageUrl}
                  alt=""
                  sizes={`${PLAYER_CIRCLE_SIZE}px`}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            )}
            {imageUrl && !hasImageFailed && (
              <Image
                src={imageUrl}
                // Empty while blurred: a broken portrait renders its alt text,
                // which would print in full the name the blur is hiding.
                alt={isBlurred ? '' : name}
                sizes={`${PLAYER_CIRCLE_SIZE}px`}
                layout="fill"
                objectFit="cover"
                className={`transition duration-300 ease-out ${
                  isPortraitLoaded
                    ? // Blurred at the same scale as an unblurred portrait, so
                      // the two sides of a transfer card frame their players
                      // identically — the blur's soft edge is absorbed by the
                      // silhouette above, not by scaling the face out of clip.
                      `scale-100 opacity-100 ${isBlurred ? CONTENT_BLUR : ''}`
                    : 'scale-150 opacity-0'
                }`}
                onLoadingComplete={() => setIsPortraitLoaded(true)}
                onError={() => setHasImageFailed(true)}
                data-cy="player-portrait"
              />
            )}
            {showPortrait && !isPortraitLoaded && <LoadingOverlay />}
          </PlayerCircle>

          {/* A withheld player takes the padlock here instead of a crest.
              Blurring the crest was not enough to hide it — a club is only
              twenty guesses wide and the shape and colours survive the blur,
              which narrows the player far more than the ticket intends. */}
          <div className="absolute -bottom-[7px] -left-[9px] z-10">
            {isBlurred ? (
              <LockCircle />
            ) : (
              <ClubBadgeCircle teamCode={teamCode} />
            )}
          </div>

          {/* The corner opposite the club badge, holding whichever of the
              three markers applies. They are mutually exclusive in practice —
              a withheld player is neither removable nor a captain — so the
              order here only settles a case that does not arise. */}
          {cornerMarker && (
            <div className="absolute -bottom-[7px] -right-[9px] z-10">
              {cornerMarker}
            </div>
          )}
        </div>

        <PlayerInfo
          name={name}
          price={price}
          teamCode={teamCode}
          blurred={isBlurred}
          compact={isCompact}
        />
      </div>
    </div>
  );
};

export default PitchPlayer;
